import React, { useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  Grid,
  Paper,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  getGalleryStatusDisplay,
  getVisibleImages,
} from "../utils/publicGalleryHelpers";

const GalleryManager = ({
  gallery,
  onEdit,
  onDelete,
  onUpload,
  onDeleteImage,
  onToggleVisibility,
  onSetCover,
  onSettingsUpdate,
}) => {
  const [tab, setTab] = useState(0);
  const statusDisplay = getGalleryStatusDisplay(gallery.status);
  const images = getVisibleImages(gallery.images);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {gallery.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {gallery.galleryCode} • {gallery.category} •{" "}
              {gallery.eventId || "No linked event"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip label={statusDisplay.label} color={statusDisplay.color} />
            <Chip
              label={gallery.isPublic ? "Public" : "Private"}
              color={gallery.isPublic ? "info" : "default"}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
          >
            Delete
          </Button>
        </Box>
      </Paper>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        <Tab label="Images" />
        <Tab label="Statistics" />
        <Tab label="Settings" />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">
              Gallery Images ({images.length})
            </Typography>
            <Button startIcon={<AddIcon />} onClick={onUpload} size="small">
              Upload Images
            </Button>
          </Box>

          {images.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No images uploaded yet
            </Typography>
          ) : (
            <ImageList cols={4} gap={12}>
              {images.map((img) => (
                <ImageListItem key={img.url}>
                  <img
                    src={img.thumbnailUrl || img.url}
                    alt=""
                    loading="lazy"
                    style={{ borderRadius: 8 }}
                  />
                  <ImageListItemBar
                    actionIcon={
                      <Box sx={{ display: "flex" }}>
                        <IconButton
                          sx={{
                            color:
                              gallery.coverImageUrl === img.url
                                ? "warning.main"
                                : "white",
                          }}
                          onClick={() => onSetCover(img.url, img.thumbnailUrl)}
                        >
                          {gallery.coverImageUrl === img.url ? (
                            <StarIcon />
                          ) : (
                            <StarBorderIcon />
                          )}
                        </IconButton>
                        <IconButton
                          sx={{ color: "white" }}
                          onClick={() => onToggleVisibility(img.url)}
                        >
                          {img.isVisible ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                        <IconButton
                          sx={{ color: "white" }}
                          onClick={() => onDeleteImage(img.url)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Gallery Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Total Images</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>{gallery.statistics?.totalImages || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Total Views</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>{gallery.statistics?.totalViews || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Total Shares</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>{gallery.statistics?.totalShares || 0}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Gallery Settings
          </Typography>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!gallery.settings?.allowDownload}
                    onChange={(e) =>
                      onSettingsUpdate({ allowDownload: e.target.checked })
                    }
                  />
                }
                label="Allow Download"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!gallery.settings?.allowComments}
                    onChange={(e) =>
                      onSettingsUpdate({ allowComments: e.target.checked })
                    }
                  />
                }
                label="Allow Comments"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!gallery.settings?.allowShare}
                    onChange={(e) =>
                      onSettingsUpdate({ allowShare: e.target.checked })
                    }
                  />
                }
                label="Allow Share"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!gallery.settings?.showWatermark}
                    onChange={(e) =>
                      onSettingsUpdate({ showWatermark: e.target.checked })
                    }
                  />
                }
                label="Show Watermark"
              />
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default GalleryManager;
